import React from "react";
import styled, { css } from "styled-components";
import { variantStyles, sizeStyles } from "../../../utils/styled";

// Button base con variantes
const StyledButton = styled.button`
    border-radius: ${props => props.theme.borderRadius.buttons};
    outline: 0;
    font-size: ${props => props.theme.typography.fontSize.sm};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    cursor: pointer;
    transition: all ${props => props.theme.transitions.normal};
    font-family: ${props => props.theme.typography.fontFamily.primary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    
    /* Aplicar variantes */
    ${props => {
        if (props.$variant === 'text') {
            return css`
                background-color: transparent;
                color: ${props.theme.colors.primary};
                border: none;
                min-width: ${props.theme.minWidths.button.text};
            `;
        }
        if (props.$variant === 'primary') {
            return css`
                background-color: ${props.theme.colors.primary};
                color: #fff;
                border: 1px solid ${props.theme.colors.primary};
                min-width: ${props.theme.minWidths.button.default};
            `;
        }
        if (props.$variant === 'danger') {
            return css`
                background-color: ${props.theme.colors.button.danger};
                color: #fff;
                border: 1px solid ${props.theme.colors.button.danger};
                min-width: ${props.theme.minWidths.button.default};
            `;
        }
        return variantStyles[props.$variant] || variantStyles.primary;
    }}
    
    /* Aplicar tamaños */
    ${props => {
        if (props.$size === 'small') {
            return css`
                padding: 5px 10px;
                font-size: ${props.theme.typography.fontSize.xs};
                height: ${props.theme.heights.button.small};
            `;
        }
        if (props.$size === 'large') {
            return css`
                padding: 10px 20px;
                font-size: ${props.theme.typography.fontSize.md};
                height: 46px;
            `;
        }
        return css`
            padding: 8px 15px;
            font-size: ${props.theme.typography.fontSize.sm};
            height: ${props.theme.heights.button.default};
        `;
    }}
    
    /* Estados */
    &:hover:not(:disabled) {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    
    &:focus {
        outline: 2px solid ${props => props.theme.colors.primary};
        outline-offset: 2px;
    }
`;

// IconButton wrapper
const IconButtonWrapper = styled.div`
    display: inline-block;
    position: relative;
`;

// IconButton styled
const StyledIconButton = styled.button`
    background-color: ${props => 
        props.$variant === 'primary' 
            ? props.theme.colors.primary 
            : props.theme.colors.button.icon
    };
    border: 1px solid ${props => 
        props.$variant === 'primary' 
            ? props.theme.colors.primary 
            : props.theme.colors.button.icon
    };
    border-radius: ${props => props.theme.borderRadius.buttons};
    height: ${props => props.theme.heights.button.icon};
    width: ${props => props.theme.heights.button.icon};
    color: ${props => 
        props.$variant === 'primary' 
            ? '#fff' 
            : props.theme.colors.primary
    };
    outline: 0;
    cursor: pointer;
    transition: all ${props => props.theme.transitions.normal};
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        height: 20px;
        width: 20px;
        
        path {
            stroke: ${props => 
                props.$variant === 'primary' 
                    ? '#fff' 
                    : props.theme.colors.primary
            };
            transition: stroke ${props => props.theme.transitions.normal};
        }
    }
    
    &:hover:not(:disabled) {
        background-color: ${props => 
            props.$variant === 'primary' 
                ? props.theme.colors.button.icon 
                : props.theme.colors.primary
        };
        border-color: ${props => 
            props.$variant === 'primary' 
                ? props.theme.colors.button.icon 
                : props.theme.colors.primary
        };
        color: ${props => 
            props.$variant === 'primary' 
                ? props.theme.colors.primary 
                : '#fff'
        };
        
        svg path {
            stroke: ${props => 
                props.$variant === 'primary' 
                    ? props.theme.colors.primary 
                    : '#fff'
            };
        }
    }
    
    ${props => props.$variant === 'danger' && css`
        svg path {
            stroke: ${props.theme.colors.button.danger};
        }
        
        &:hover:not(:disabled) {
            background-color: ${props.theme.colors.button.danger};
            border-color: ${props.theme.colors.button.danger};
            color: #fff;
            
            svg path {
                stroke: #fff;
            }
        }
    `}
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

// Tooltip para IconButton
const Tooltip = styled.span`
    visibility: hidden;
    opacity: 0;
    width: max-content;
    max-width: 200px;
    background-color: ${props => props.theme.colors.dark};
    color: #fff;
    text-align: center;
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.sm};
    position: absolute;
    z-index: ${props => props.theme.zIndex.tooltip};
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    transition: opacity ${props => props.theme.transitions.slow};
    font-size: ${props => props.theme.typography.fontSize.xs};
    pointer-events: none;
    
    ${IconButtonWrapper}:hover & {
        visibility: visible;
        opacity: 1;
    }
`;

// Componentes exportados
export const PrimaryButton = ({ text, onClick, type, ...props }) => {
    return (
        <StyledButton 
            onClick={onClick} 
            $variant={type === "text" ? "text" : "primary"}
            {...props}
        >
            {text}
        </StyledButton>
    );
};

export const IconButton = ({ children, onClick, type, style, tooltip, ...props }) => {
    const variant = type === "primary" ? "primary" : type === "danger" ? "danger" : "icon";
    
    return (
        <IconButtonWrapper style={style}>
            <StyledIconButton
                onClick={onClick}
                $variant={variant}
                {...props}
            >
                {children}
            </StyledIconButton>
            {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </IconButtonWrapper>
    );
};

IconButton.defaultProps = {
    style: {}
};

export const DangerButton = ({ text, onClick, type, ...props }) => {
    return (
        <StyledButton 
            onClick={onClick} 
            $variant={type === "text" ? "text" : "danger"}
            {...props}
        >
            {text}
        </StyledButton>
    );
};

// Button genérico con todas las variantes
export const Button = ({ variant = 'primary', size = 'medium', disabled, loading, onClick, children, style, ...props }) => {
    return (
        <StyledButton
            $variant={variant}
            $size={size}
            disabled={disabled || loading}
            onClick={onClick}
            style={style}
            {...props}
        >
            {loading && <span style={{ marginRight: '8px' }}>⏳</span>}
            {children}
        </StyledButton>
    );
};

Button.defaultProps = {
    size: 'medium',
    disabled: false,
    loading: false,
    onClick: () => {},
};